import { profileBookingSearchLoadingSelector } from '@/features/loading/loadingSelectors';
import {
  getProfileActiveTickets,
  getProfileHistoryTickets,
} from '@/features/profile/profileActions';
import {
  activeTicketsProfileSelector,
  historyTicketsProfileSelector,
} from '@/features/profile/profileSelectors';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import BookingItem from '@/routes/account/layout/BookingItem';
import BookingNoItems from '@/routes/account/layout/BookingNoItems';
import { useDispatch, useSelector } from '@/store/utils';
import type { ProfileBookingTimeType } from '@/types/profile';
import { useCallback, useMemo, type FC } from 'react';

interface TicketsListProps {
  timeType: ProfileBookingTimeType;
}

const TicketsList: FC<TicketsListProps> = ({ timeType }) => {
  const dispatch = useDispatch();

  const isActive = timeType === 'active';

  const loading = useSelector(profileBookingSearchLoadingSelector);
  const tickets = useSelector(
    isActive ? activeTicketsProfileSelector : historyTicketsProfileSelector
  );

  const hasMore = useMemo(() => {
    if (!tickets) return false;
    return tickets.data.length < tickets.totalCount;
  }, [tickets]);

  const handleLoadMore = useCallback(() => {
    dispatch(isActive ? getProfileActiveTickets() : getProfileHistoryTickets());
  }, [dispatch, isActive]);

  const loadMoreRef = useInfiniteScroll({
    hasMore,
    loading,
    onLoadMore: handleLoadMore,
  });

  if (!tickets?.data.length) {
    return <BookingNoItems bookingType="tickets" timeType={timeType} />;
  }

  return (
    <div className="flex w-full flex-col laptop:mt-2 laptop:max-w-[690px]">
      <div className="flex w-full flex-col gap-4">
        {tickets.data.map((ticket, index) => (
          <BookingItem
            key={`${ticket.id}-${index}`}
            booking={ticket}
            isActive={isActive}
            isTicket
          />
        ))}
      </div>
      {hasMore && <div ref={loadMoreRef} />}
    </div>
  );
};

export default TicketsList;
